import React from 'react';
import { getLabelStyle } from './widgetLabelHelpers';

/**
 * BaseScanGridWidget
 * Shared UI for 3x3 Grid Scan Widgets
 */
export const BaseScanGridWidget = ({ 
    findings, 
    title, 
    auditScore, 
    onCardClick, 
    style = {}, 
    config = {},
    theme = {} 
}) => {
    const statusColors = {
        'Green': style.auditColorGreen || theme?.statusColors?.green || '#40B657',
        'Yellow': style.auditColorYellow || theme?.statusColors?.yellow || '#FCC419',
        'Red': style.auditColorRed || theme?.statusColors?.red || '#FF0000',
        'Unknown': '#9ca3af'
    };

    // Default layout if not provided
    const defaultLayout = [
        { id: 'title', label: 'Title', visible: true },
        { id: 'grid', label: 'Findings Grid', visible: true },
        { id: 'legend', label: 'Status Legend', visible: true },
        { id: 'score', label: 'Audit Score Bar', visible: true }
    ];

    const layout = config.layout || defaultLayout;

    const renderComponent = (componentId) => {
        const item = layout.find(l => l.id === componentId);
        if (item && item.visible === false) return null;

        const widgetType = config.widgetType || 'combined-scan-grid';

        const getFlexAlign = (textAlign) => {
            if (textAlign === 'left') return 'flex-start';
            if (textAlign === 'right') return 'flex-end';
            return 'center';
        };

        switch (componentId) {
            case 'title':
                return (
                    <div key="title" style={{ marginBottom: '16px' }}>
                        <h2 
                            style={{
                                margin: '0',
                                letterSpacing: '0.05em',
                                ...getLabelStyle(config, 'title', {
                                    fontSize: '28px',
                                    fontWeight: '700',
                                    color: '#6b7280',
                                    textAlign: 'left'
                                }, widgetType)
                            }}
                        >
                            {title}
                        </h2>
                    </div>
                );
            case 'grid':
                const headerStyle = getLabelStyle(config, 'findingHeaders', {
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '700',
                    textAlign: 'center'
                }, widgetType);

                const descStyle = getLabelStyle(config, 'findingDescriptions', {
                    color: '#1f2937',
                    fontSize: '11px',
                    textAlign: 'center',
                    fontWeight: '500'
                }, widgetType);

                const showHeaders = config.showFindingHeaders !== false;
                const showDescriptions = config.showFindingDescriptions !== false;
                const isMinimalMode = !showHeaders && !showDescriptions;

                return (
                    <div key="grid" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(3, 1fr)', 
                        gap: '12px',
                        marginBottom: '16px'
                    }}>
                        {findings.map((finding) => {
                            const borderColor = statusColors[finding.status] || statusColors['Unknown'];
                            const activeBorderColor = (style.itemBorderColorOverride && style.itemBorderColor) ? style.itemBorderColor : borderColor;
                            const borderStyle = style.itemBorderStyle || 'solid';
                            const borderWidth = style.itemBorderWidth !== undefined ? style.itemBorderWidth : 0;
                            
                            return (

                                <div 
                                    key={finding.id}
                                    onClick={() => onCardClick && onCardClick(finding)}
                                    style={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        height: '100%', 
                                        border: `${borderWidth}px ${borderStyle} ${isMinimalMode ? 'transparent' : activeBorderColor}`, 
                                        borderRadius: `${style.itemBorderRadius || 8}px`, 
                                        overflow: 'hidden',
                                        backgroundColor: isMinimalMode ? borderColor : 'transparent',
                                        aspectRatio: (isMinimalMode || (showHeaders && !showDescriptions)) ? '1/1' : 'auto',
                                        minHeight: (isMinimalMode || (showHeaders && !showDescriptions)) ? '60px' : 'auto',
                                        containerType: 'inline-size',
                                        position: 'relative',
                                        cursor: 'pointer'
                                    }}
                                >

                                    {/* Manual Override Badge */}
                                    {finding.isManual && !isMinimalMode && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: finding.isDataSource ? '50px' : '4px',
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                            color: '#fff',
                                            fontSize: '8px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontWeight: '600',
                                            zIndex: 10
                                        }}>
                                            MANUAL
                                        </div>
                                    )}

                                    {/* Data Source Badge */}
                                    {finding.isDataSource && !isMinimalMode && (
                                        <div style={{
                                            position: 'absolute',
                                            top: '4px',
                                            right: '4px',
                                            backgroundColor: 'rgba(37,99,235,0.8)',
                                            color: '#fff',
                                            fontSize: '8px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            fontWeight: '600',
                                            zIndex: 10
                                        }}>
                                            DATA SOURCE
                                        </div>
                                    )}
                                    
                                    {/* Colored Header Bar / Indicator */}
                                    {(showHeaders || (showDescriptions && !showHeaders)) && (
                                        <div 
                                            style={{
                                                backgroundColor: borderColor,
                                                padding: showHeaders ? '12px' : '0',
                                                height: showHeaders ? (showDescriptions ? 'auto' : '100%') : '8px',
                                                minHeight: showHeaders ? (showDescriptions ? '48px' : '100%') : '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: getFlexAlign(headerStyle.textAlign)
                                            }}
                                        >
                                            {showHeaders && (
                                                <h3 
                                                    style={{
                                                        margin: '0',
                                                        lineHeight: '1.2',
                                                        width: '100%',
                                                        wordBreak: 'break-word',
                                                        hyphens: 'auto',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 4,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        fontSize: 'clamp(10px, 12cqi, 18px)',
                                                        ...headerStyle,
                                                        // Ensure custom fontSize from config still wins if provided
                                                        ...(config?.labelStyles?.findingHeaders?.fontSize ? { fontSize: config.labelStyles.findingHeaders.fontSize } : {})
                                                    }}
                                                >
                                                    {finding.title}
                                                </h3>
                                            )}
                                        </div>
                                    )}
                                    
                                    {/* White Content Box */}
                                    {showDescriptions && (
                                        <div 
                                            style={{
                                                backgroundColor: style.itemDescBgColor || '#ffffff',
                                                padding: '16px',
                                                flex: '1',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: getFlexAlign(descStyle.textAlign),
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <p 
                                                style={{
                                                    lineHeight: '1.5',
                                                    margin: '0',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    width: '100%',
                                                    fontSize: 'clamp(9px, 8cqi, 14px)',
                                                    ...descStyle,
                                                    // Ensure custom fontSize from config still wins if provided
                                                    ...(config?.labelStyles?.findingDescriptions?.fontSize ? { fontSize: config.labelStyles.findingDescriptions.fontSize } : {})
                                                }}
                                            >
                                                {finding.description}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );

            case 'legend':
                const legendStyle = getLabelStyle(config, 'legend', {
                    fontSize: '11px',
                    color: '#6b7280'
                }, widgetType);

                return (
                    <div key="legend" style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: getFlexAlign(legendStyle.textAlign || 'center'),
                        gap: '32px',
                        marginBottom: '24px',
                        paddingLeft: '8px',
                        paddingRight: '8px',
                        ...legendStyle
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: statusColors['Red'] }}></div>
                            <span>Requires Immediate Attention</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: statusColors['Yellow'] }}></div>
                            <span>Needs Improvement</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: '16px', height: '16px', backgroundColor: statusColors['Green'] }}></div>
                            <span>Satisfactory</span>
                        </div>
                    </div>
                );
            case 'score':
                const scoreStyle = getLabelStyle(config, 'auditScore', {
                    fontSize: '13px', 
                    color: '#9ca3af',
                    fontWeight: '600'
                }, widgetType);

                return (
                    <div key="score" style={{ marginTop: '16px' }}>
                        {/* Divider Line */}
                        <div style={{ 
                            borderTop: '1px solid #d1d5db',
                            marginBottom: '16px'
                        }}></div>
                        
                        <div style={{ textAlign: scoreStyle.textAlign || 'center', marginBottom: '12px' }}>
                            <span style={{ 
                                letterSpacing: '0.05em',
                                display: 'inline-block',
                                width: scoreStyle.textAlign ? '100%' : 'auto',
                                ...scoreStyle
                            }}>
                                Audit Score
                            </span>
                        </div>
                        
                        {/* Score Bar */}
                        <div style={{ 
                            position: 'relative',
                            height: '32px',
                            background: `linear-gradient(to right, ${statusColors['Red']} 0%, ${statusColors['Yellow']} 50%, ${statusColors['Green']} 100%)`,
                            borderRadius: '2px'
                        }}>
                            {/* Score Indicator */}
                            <div 
                                style={{ 
                                    position: 'absolute',
                                    top: '0',
                                    bottom: '0',
                                    left: `${auditScore}%`,
                                    transform: 'translateX(-50%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <div style={{
                                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                    ...getLabelStyle(config, 'auditScoreValue', {
                                        fontSize: '24px',
                                        fontWeight: '700',
                                        color: '#ffffff'
                                    }, widgetType)
                                }}>
                                    {auditScore}
                                </div>
                            </div>
                        </div>
                        
                        {/* Scale Labels */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            fontSize: '11px',
                            color: '#9ca3af',
                            marginTop: '8px'
                        }}>
                            <span>0</span>
                            <span>50</span>
                            <span>100</span>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="w-full font-sans" style={{ backgroundColor: '#ffffff', padding: '16px', minHeight: 'fit-content', ...style }}>
            {layout.map(item => renderComponent(item.id))}
        </div>
    );
};
